import React from 'react';
import styled from 'styled-components';

import Button from '#/components/basic/Button/Button';
import ToggleSlider from '#/components/basic/inputs/ToggleSlider/ToggleSlider';
import SimpleModalOrMobilePage from '#/components/basic/modal/SimpleModalOrMobilePage';
import { wipeChartDefaultPersistedItems } from '#/components/chart/persistance/generalStorage';
import { resetChartSettings } from '#/components/chart/persistance/settingsAdapter';
import { KATANA_PERPS } from '#/config/constants';
import { useNavigateToPrevious } from '#/lib/redirect';
import {
  changeSkipBuySellConfirmation,
  closedLargeSpreadMarketOrderModal,
  skipCancelOrderConfirmation,
  skipDesktopQRConnectModal,
  toggleNotifications,
} from '#/reduxState/exchange/userGuideReducer';
import { staticDispatch, useSelector } from '#/reduxState/store';
import { openSettings } from '#/reduxState/userSettings/userSettingsReducer';

const Wrap = styled.div`
  max-width: 700px;
  margin: 0 auto;
`;

const Row = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid var(--separator);
  margin-bottom: 16px;

  display: flex;
  justify-content: space-between;
`;

const Heading = styled.h2`
  text-transform: uppercase;
  padding: 0;
`;

const SubTitle = styled.div`
  color: var(--gray4c);
  /* font-size: var(--small); */
  line-height: normal;
  padding-top: 4px;
`;

interface SettingsItemProps {
  title: string;
  subTitle?: string;
  checked: boolean;
  onChange: () => void;
}

function SettingsItem({
  title,
  subTitle,
  checked,
  onChange,
}: SettingsItemProps) {
  return (
    <Row>
      <div>
        {title}
        <SubTitle>{subTitle}</SubTitle>
      </div>
      <div>
        <ToggleSlider checked={checked} onChange={onChange} />
      </div>
    </Row>
  );
}

export default function SettingsModal() {
  const isMobile = useSelector(state => state.screen.isMobile);
  const userGuide = useSelector(state => state.exchange.userGuide);

  const navigateBackOnMobile = useNavigateToPrevious('/mobile-more');
  const handleCloseOnDesktop = () => staticDispatch(openSettings(false));

  return (
    <SimpleModalOrMobilePage
      title="Settings"
      size="medium"
      onClose={isMobile ? navigateBackOnMobile : handleCloseOnDesktop}
      verticalAlignOnMobile="ceneteredDiv"
    >
      <Wrap>
        <Heading>Orders</Heading>
        <SettingsItem
          checked={!userGuide.skipBuySellConfirmation}
          onChange={() => {
            staticDispatch(
              changeSkipBuySellConfirmation(!userGuide.skipBuySellConfirmation)
            );
          }}
          title="Place Order Confirmation"
          subTitle="Each Order needs to be confirmed before placing."
        />
        <SettingsItem
          checked={!userGuide.skipCancelOrderConfirm}
          onChange={() => {
            staticDispatch(
              skipCancelOrderConfirmation(!userGuide.skipCancelOrderConfirm)
            );
          }}
          title="Cancel Order Confirmation"
          subTitle="Each order cancellation needs to be confirmed."
        />
        <SettingsItem
          checked={!userGuide.closedLargeSpreadMarketOrderWarning}
          onChange={() => {
            staticDispatch(
              closedLargeSpreadMarketOrderModal(
                !userGuide.closedLargeSpreadMarketOrderWarning
              )
            );
          }}
          title="Place large spread market order Confirmation"
          subTitle="When spread on the market is high, Market orders will match with
          existing orders on the books."
        />
        <Heading>Wallet</Heading>
        <SettingsItem
          checked={!userGuide.skipQrConnectModal}
          onChange={() => {
            staticDispatch(
              skipDesktopQRConnectModal(!userGuide.skipQrConnectModal)
            );
          }}
          title={`Show ${KATANA_PERPS} Connect after unlock`}
          subTitle="Screen to help sync Desktop wallet to Mobile device using QR code."
        />
        <Heading>Notifications</Heading>
        <SettingsItem
          checked={!userGuide.notificationsDisabled}
          onChange={() => {
            staticDispatch(
              toggleNotifications(userGuide.notificationsDisabled)
            );
          }}
          title="Notifications enabled"
        />
        <Heading>Chart</Heading>
        <br />
        Reset TradingView Chart settings
        <br />
        <br />
        <Button
          onClick={() => {
            wipeChartDefaultPersistedItems();
            resetChartSettings();
            window.location.reload();
          }}
          inline
          widePadding
          bordered
        >
          Reset Chart
        </Button>
        <br />
        <small>Requires page reload</small>
      </Wrap>
    </SimpleModalOrMobilePage>
  );
}
